/**
 * Resolve a `CabinetWallRef` ({ id, kind }) to a canonical engine wall
 * `{ id, alignment: { bottomLeft, bottomRight }, ... }` in the SAME frame the
 * auto generator used — so an edit-time re-solve reflows against identical
 * geometry and stored `wallParam`s never drift.
 *
 * This is the "make-or-break" parity primitive for auto-kitchen editing
 * (claude-docs/2026-07-08-phase2-editability-plan.md §2). Both the run-deriver
 * (`toRuns.js`) and the edit-time resolver MUST go through here.
 *
 * ## Why `real` walls resolve to the FINAL (clipped) walls, not `room.walls`
 *
 * `materializeCandidate` does NOT build its `wallById` from `room.walls`
 * verbatim — it builds it from
 *   `getKitchenFinalWalls({ ...room, walls: dedupeRoomWalls(room.walls) })`
 * (materializeCandidate.js:764-770). `getKitchenFinalWalls` can *clip* a wall to
 * the portion that bounds the kitchen polygon (`clipWallToRange`), which keeps
 * the wall's `id` but moves its `bottomLeft`/`bottomRight`. Every cabinet
 * position and every appliance `wallParam` generation stored is measured from
 * that clipped `bottomLeft`. Resolving `real` refs against raw `room.walls`
 * would therefore reintroduce a `t1 * wallLen` offset on the leading edge and
 * shift every stored param — the exact "stored params vs cabinet side disagree
 * on re-solve" bug (cabinet-runs/overview.md gotcha). So we replicate
 * generation's wall pipeline exactly and key by id.
 *
 * `peninsula` / `island` walls are synthesized (not on the room boundary) and
 * their geometry is persisted inline on the `cabinetWalls` entry — that inline
 * `alignment` is the only durable source, so imaginary refs resolve from it.
 *
 * Pure. No I/O.
 */

const { getKitchenFinalWalls } = require('../kitchenPerimeter');
const { dedupeRoomWalls } = require('../wallDedupe');

/**
 * Build a resolver bound to one room + cabinetLayout. Computes the final-wall
 * pipeline ONCE and returns a `resolve(ref)` closure (cheap per-ref lookups)
 * plus the computed maps for callers that need to enumerate.
 *
 * @param {Object} room           room doc (needs `walls`, and whatever
 *   `getKitchenFinalWalls` reads: `sections`, `kitchenArea`, floor).
 * @param {Object} [cabinetLayout] room.cabinetLayout (for imaginary walls).
 *   Defaults to `room.cabinetLayout`.
 * @returns {{
 *   resolve: (ref: {id:string, kind:string}) => Object|null,
 *   finalWalls: Object[],
 *   finalById: Map<string, Object>,
 *   cabinetWallById: Map<string, Object>,
 * }}
 */
function buildCabinetWallResolver(room, cabinetLayout) {
  const layout = cabinetLayout || room?.cabinetLayout || null;

  // Replicate materializeCandidate.js:764-770 exactly so `real` refs resolve to
  // the identical clipped wall objects generation stamped geometry against.
  const deduped = dedupeRoomWalls(room?.walls || []);
  const finalWalls = getKitchenFinalWalls({
    ...(room || {}),
    walls: deduped,
  });
  const finalById = new Map((finalWalls || []).map((w) => [w.id, w]));
  const dedupedById = new Map(deduped.map((w) => [w.id, w]));

  const cabinetWallById = new Map(
    (layout?.cabinetWalls || []).map((cw) => [cw.id, cw])
  );

  function resolve(ref) {
    if (!ref || typeof ref.id !== 'string') return null;
    if (ref.kind === 'real') {
      // Identical object to generation's wallById.get(id). If the kitchen
      // polygon dropped this wall — a wall the layout DID use, whose room
      // wall a wall move has just carried off the section's boundary — the
      // full room wall stands in, so the run on it re-solves instead of
      // orphaning and deleting its cabinets (found 2026-08-30: a vertex
      // move emptied the sink wall). A wall the layout never used still
      // returns null; callers surface a drop rather than guessing.
      const clipped = finalById.get(ref.id);
      if (clipped) return clipped;
      // Only a wall the LAYOUT used: on the scan path (no layout) the clip
      // still decides which walls carry cabinetry.
      if (layout && cabinetWallById.has(ref.id)) {
        return dedupedById.get(ref.id) || null;
      }
      return null;
    }
    // peninsula | island — geometry lives inline on the cabinetWalls entry.
    const cw = cabinetWallById.get(ref.id);
    if (!cw || !cw.alignment) return null;
    return {
      id: cw.id,
      kind: cw.kind,
      alignment: cw.alignment,
      workingNormal: cw.workingNormal,
      cabinetDepth: cw.cabinetDepth,
      doubleSided: cw.doubleSided,
      // A peninsula / island wall is VIRTUAL, and several builders branch on
      // that: `buildCountertopRuns` adds the 1" back-panel cover only for a
      // virtual wall, and `materializeCandidate` skips virtual walls in its
      // wall-body, corner and backsplash passes. Resolving one without the flag
      // meant an edit-time re-solve treated a peninsula as a REAL wall — the new
      // slab ended 1" short of the back panel the generator had already built,
      // and left it standing proud of the counter.
      isVirtual: cw.kind !== 'real',
      // A seated arm: its top is rebuilt as one slab with the knee overhang
      // (`seatedPeninsulaTop.seatRunArms`, from `fillRun`).
      ...(cw.seating ? { seating: cw.seating } : {}),
      // A continuation piece's cut where it meets its run at an angle
      // (`seatedSlabCut.js`): its seated slab and back panel stop on that line.
      ...(cw.cuts ? { cuts: cw.cuts } : {}),
      // A continuation piece (`toRuns.joinsStraight`).
      ...(cw.continuation ? { continuation: true } : {}),
    };
  }

  return { resolve, finalWalls, finalById, cabinetWallById };
}

/**
 * Convenience single-shot resolve. Prefer `buildCabinetWallResolver` when
 * resolving many refs for one room (this rebuilds the final-wall pipeline each
 * call).
 *
 * @param {{id:string, kind:string}} ref
 * @param {Object} room
 * @param {Object} [cabinetLayout]
 * @returns {Object|null}
 */
function resolveCabinetWall(ref, room, cabinetLayout) {
  return buildCabinetWallResolver(room, cabinetLayout).resolve(ref);
}

module.exports = { buildCabinetWallResolver, resolveCabinetWall };
